import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Button, Divider, Grid, Skeleton, Tooltip, Typography } from "@mui/material";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchLatestEmployeeBidChoiceEventsByEidAndDate } from "../../api/bid.js";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { selectWebSocketEventData } from "../../slices/webSocketEvent/webSocketEventSlice.js";
import { BID_DATE_TYPE, BID_STATUS } from "../../util/bid/bidConstants.js";
import { getScrubbedBidChoiceEvents } from "../../util/bid/bidScrubber.js";
import { filterDeletedBidChoiceEvents } from "../../util/bid/filterBids.js";
import { TODAY } from "../../util/dateHelper.js";
import { getShiftDate, set_time_to_end_of_day, set_time_to_midnight } from "../../util/dateTimeFormatter.js";
import { mergeWebSocketEmpBidChoices } from "../../util/webSocketUtils.js";
import DashboardEmployeeBid from "./DashboardEmployeeBid.jsx";

const DashboardEmployeeBidsCard = (props) => {
  const userSession = useSelector(selectUserSession);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [bidEvents, setBidEvents] = useState([]);

  const isFirstLoadRef = useRef(true);
  const bidWebSocketDataRef = useRef({});

  const START_DATE = set_time_to_midnight(TODAY(userSession.activeStation));
  const END_DATE = set_time_to_end_of_day(START_DATE.plus({ month: 1 }));

  useEffect(() => {
    fetchBidData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bidWebSocketDataRef.current = webSocketEventData.bidEmpEvents;
    if (Object.values(webSocketEventData.bidEmpEvents).length > 0) {
      if (!isFirstLoadRef.current) {
        setBidEvents((prevState) => {
          let mergedEmployeeBidChoices = mergeWebSocketEmpBidChoices(webSocketEventData.bidEmpEvents, prevState);
          mergedEmployeeBidChoices = filterDeletedBidChoiceEvents(mergedEmployeeBidChoices);
          return mergedEmployeeBidChoices;
        });
        bidWebSocketDataRef.current = {};
      }
    }
    isFirstLoadRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.bidEmpEvents]);

  const fetchBidData = async () => {
    try {
      const response = await fetchLatestEmployeeBidChoiceEventsByEidAndDate(
        userSession.eid,
        BID_DATE_TYPE.BID_EFFECTIVE_DATE,
        START_DATE,
        END_DATE
      );
      const data = await response.json();
      let bidEvents = getScrubbedBidChoiceEvents(data.events);
      bidEvents = mergeWebSocketEmpBidChoices(bidWebSocketDataRef.current, bidEvents);
      bidEvents = filterDeletedBidChoiceEvents(bidEvents);
      bidEvents = bidEvents.filter((bid) => bid.status === BID_STATUS.OPEN);
      setBidEvents(bidEvents);
    } catch (error) {
      console.error("Error fetching bid choice events:", error);
      setFetchError("Unexpected Error");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <Skeleton />;

  if (bidEvents.length === 0) return "";

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: "secondary.swaGray1",
        borderRadius: "5px",
        display: "flex",
        flexDirection: "column",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        paddingTop: "1rem",
        paddingBottom: "1rem",
      }}
    >
      <Grid
        container
        sx={{
          textAlign: "left",
          marginBottom: ".5rem",
        }}
      >
        <Grid item xs={6}>
          <Typography variant="h6" component="div" style={{ textAlign: "left", fontWeight: "bold" }}>
            Open Bids{" "}
            <Tooltip
              title={`Displaying all of your Bids between ${getShiftDate(START_DATE)}, ${START_DATE.toFormat(
                "HH:mm ZZZZ"
              )} to ${getShiftDate(END_DATE)}, ${END_DATE.toFormat("HH:mm ZZZZ")}`}
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{ color: "secondary.swaGray5", verticalAlign: "middle", marginLeft: "5px", marginBottom: "5px" }}
              />
            </Tooltip>
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: "flex", justifyContent: "end" }}>
          <Button href={`#/myBids`} size="small">
            <Typography sx={{ color: "primary.swaBlue", textDecoration: "underline" }}>See All</Typography>
            <Typography sx={{ color: "primary.swaBlue" }}>→</Typography>
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>
      {fetchError !== "" ? (
        <Alert
          variant="filled"
          severity="error"
          sx={{ display: "flex", width: "200px", alignSelf: "center", margin: "1rem" }}
        >
          {fetchError}
        </Alert>
      ) : bidEvents.length === 0 ? (
        <Typography sx={{ margin: "30px" }}>No bids available</Typography>
      ) : (
        bidEvents.map((bid, i) => (
          <Box key={bid.referenceNumber}>
            {i !== 0 && <Divider sx={{ marginTop: "20px" }} />}
            <DashboardEmployeeBid bid={bid} isDesktopView={props.isDesktopView} />
          </Box>
        ))
      )}
    </Paper>
  );
};

DashboardEmployeeBidsCard.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
};

export default DashboardEmployeeBidsCard;
